/**
 * BI > Extrato SKU — entradas e saídas por SKU / lote fabricante (#MT-8C58776).
 * Visual idêntico ao Kardex (BI). API: GET .../reports/sku-extract
 */
import api from '../api.js';
import { translate } from '../../versa/filter.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import { escapeHtml as esc } from '../utils/escape-html.js';
import { formatBiHttpError } from './bi-http-error.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function skuLabel(p) {
	if (!p?.sku) return '';
	return Array.isArray(p.sku) ? p.sku.join('') : String(p.sku);
}

/** Evita nomes longos (ex. placeholders XXX…) quebrarem o layout do BI. */
function shortLabel(text, max = 72) {
	const s = String(text || '').replace(/\s+/g, ' ').trim();
	if (s.length <= max) return s;
	return `${s.slice(0, max - 1)}…`;
}

function fmtDate(v) {
	if (!v) return '';
	const s = String(v);
	if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
		const [y, m, d] = s.slice(0, 10).split('-');
		return `${d}/${m}/${y}`;
	}
	return s;
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	const list = await res.json();
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

async function searchProducts(companyId, term) {
	const needle = String(term || '').trim();
	if (!companyId || !needle) return [];
	const base = {
		'field-0': 'company_id', 'operator-0': 'eq', 'value-0': companyId,
		'connector-1': 'e',
	};
	const filters = [
		translate({ ...base, 'field-1': 'sku', 'operator-1': 'co', 'value-1': needle }),
		translate({ ...base, 'field-1': 'name', 'operator-1': 'co', 'value-1': needle }),
	];
	if (/^\d+$/.test(needle)) {
		filters.unshift(translate({ ...base, 'field-1': 'sku', 'operator-1': 'eq', 'value-1': needle }));
		if (needle.length <= 7) {
			filters.unshift(translate({ ...base, 'field-1': 'id', 'operator-1': 'eq', 'value-1': needle }));
		}
	}
	const seen = new Set();
	const out = [];
	for (const fe of filters) {
		try {
			const rows = await api.call(
				api.list_products,
				`?filter=${encodeURIComponent(fe)}&limit=25&include_disabled=1`,
			);
			if (!Array.isArray(rows)) continue;
			for (const p of rows) {
				if (p?.id == null || seen.has(p.id)) continue;
				seen.add(p.id);
				out.push(p);
			}
			if (out.length >= 25) break;
		} catch (e) {
			console.warn('Extrato SKU busca produto', e);
		}
	}
	return out;
}

function collectQuery(node, format) {
	const companyId = $('#exsku-company', node)?.value?.trim();
	if (!companyId) {
		notify('Selecione a empresa.', 'Extrato SKU');
		return null;
	}
	const productId = $('#exsku-product-id', node)?.value?.trim();
	const sku = $('#exsku-sku', node)?.value?.trim();
	if (!productId && !sku) {
		notify('Busque e selecione o produto (ou informe o SKU).', 'Extrato SKU');
		return null;
	}
	const sp = new URLSearchParams();
	sp.set('format', format);
	if (productId) sp.set('product_id', productId);
	else sp.set('sku', sku);
	const lot = $('#exsku-lot', node)?.value?.trim();
	if (lot) sp.set('lot_number', lot);
	return { companyId, qs: sp.toString(), skuHint: sku || productId || 'sku' };
}

function renderResult(host, data) {
	if (!data?.product) {
		host.innerHTML = '<p class="mts" style="color:#64748b;padding:12px 14px">Sem dados.</p>';
		return;
	}
	const p = data.product;
	const t = data.totals || {};
	const entries = Array.isArray(data.entries) ? data.entries : [];
	const exits = Array.isArray(data.exits) ? data.exits : [];

	let html = `
		<div class="kdx-meta">
			<span>
				<strong>${esc(p.sku)}</strong> — ${esc(shortLabel(p.produto, 80))}
				<span style="color:#64748b;font-weight:400"> · ${esc(p.fabricante || '—')} · ${esc(p.empresa || '')}</span>
			</span>
			<span>
				Entradas ${esc(t.entradas)} (qtd ${esc(t.qtd_entrada)})
				· Saídas ${esc(t.saidas)} (qtd sep. ${esc(t.qtd_saida_separada)})
			</span>
		</div>
	`;
	if (data.lot_filter) {
		html += `<div style="font-size:0.82rem;padding:6px 14px;background:#f1f5f9;border-bottom:1px solid #e2e8f0;color:#334155">
			Lote fab.: <strong>${esc(data.lot_filter)}</strong>
		</div>`;
	}

	html += '<div class="kdx-filter-title" style="padding:12px 14px 6px;margin:0">Entradas</div>';
	if (!entries.length) {
		html += '<p class="mts" style="color:#64748b;padding:8px 14px 16px">Nenhuma entrada para este filtro.</p>';
	} else {
		html += `<div style="overflow:auto;max-height:36vh;padding:0 0 8px">
			<table class="kdx-table">
			<thead><tr>
				<th>Entrada</th><th>NF</th><th>Série</th><th>Fornecedor</th>
				<th>Lote fab.</th><th class="tar">Qtd</th><th>Data</th><th>Status</th>
			</tr></thead><tbody>`;
		for (const e of entries) {
			html += `<tr>
				<td>${esc(e.entrada_sial)}</td>
				<td class="kdx-kardex-nf">${esc(e.nf_entrada)}</td>
				<td>${esc(e.serie_nf)}</td>
				<td title="${esc(e.fornecedor)}">${esc(shortLabel(e.fornecedor, 40))}</td>
				<td>${esc(e.lote_fab)}</td>
				<td class="tar kdx-qty-in">${esc(e.quantidade)}</td>
				<td>${esc(fmtDate(e.data))}</td>
				<td>${esc(e.status)}</td>
			</tr>`;
		}
		html += '</tbody></table></div>';
	}

	html += '<div class="kdx-filter-title" style="padding:12px 14px 6px;margin:0">Saídas</div>';
	if (!exits.length) {
		html += '<p class="mts" style="color:#64748b;padding:8px 14px 16px">Nenhuma saída para este filtro.</p>';
	} else {
		html += `<div style="overflow:auto;max-height:36vh;padding:0 0 8px">
			<table class="kdx-table">
			<thead><tr>
				<th>Saída</th><th>Pedido</th><th>Destinatário</th><th>Lote fab.</th>
				<th class="tar">Pedida</th><th class="tar">Separada</th>
				<th>NF saída</th><th>Data</th><th>Status</th>
			</tr></thead><tbody>`;
		for (const e of exits) {
			html += `<tr>
				<td>${esc(e.saida)}</td>
				<td>${esc(e.pedido)}</td>
				<td title="${esc(e.destinatario)}">${esc(shortLabel(e.destinatario, 40))}</td>
				<td>${esc(e.lote_fab)}</td>
				<td class="tar">${esc(e.qtd_pedida)}</td>
				<td class="tar kdx-qty-out">${esc(e.qtd_separada)}</td>
				<td class="kdx-kardex-nf">${esc(e.nf_saida)}</td>
				<td>${esc(fmtDate(e.data))}</td>
				<td>${esc(e.status)}</td>
			</tr>`;
		}
		html += '</tbody></table></div>';
	}
	host.innerHTML = html;
}

export default async function bi_sku_extract() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.kdx-wrap { max-width: 96rem; margin: 0 auto; }
			.kdx-header { background: #1e3a5f; color: #fff; padding: 14px 18px; border-radius: 0 0 10px 10px; margin-bottom: 1rem; }
			.kdx-header h1 { font-size: 1.15rem; margin: 0; font-weight: 600; }
			.kdx-header .sub { font-size: 0.8rem; opacity: 0.92; margin-top: 4px; line-height: 1.4; }
			.kdx-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; margin-bottom: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
			.kdx-filter-title { font-size: 0.7rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
			.kdx-filters { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-end; }
			.kdx-filters label { display: flex; flex-direction: column; font-size: 0.85rem; gap: 2px; color: #334155; }
			.kdx-filters input, .kdx-filters select {
				padding: 4px 8px; font-size: 0.85rem; min-height: 2rem;
				border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; color: #0f172a;
			}
			.kdx-filters input:focus, .kdx-filters select:focus {
				outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.2);
			}
			.kdx-prod-pick { max-height: 220px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; margin-top: 4px; display: none; }
			.kdx-prod-pick button {
				display: block; width: 100%; text-align: left; border: 0;
				border-bottom: 1px solid #f1f5f9; padding: 8px 12px; background: #fff;
				cursor: pointer; font-size: 0.85rem; color: #0f172a;
			}
			.kdx-prod-pick button:hover { background: #f0f9ff; }
			.kdx-qty-in { color: #047857; font-weight: 700; }
			.kdx-qty-out { color: #c2410c; font-weight: 700; }
			#workarea table.kdx-table {
				width: 100%; border-collapse: collapse; font-size: 0.82rem;
			}
			#workarea table.kdx-table th {
				text-align: left; background: #f8fafc; color: #475569; font-weight: 600;
				padding: 8px 10px; border-bottom: 1px solid #e2e8f0; white-space: nowrap;
				position: sticky; top: 0; z-index: 1;
			}
			#workarea table.kdx-table td {
				padding: 7px 10px; border-bottom: 1px solid #f1f5f9; color: #0f172a; vertical-align: top;
			}
			#workarea table.kdx-table tr:hover td { background: #f8fafc; }
			#workarea table.kdx-table td.tar,
			#workarea table.kdx-table th.tar { text-align: right; font-variant-numeric: tabular-nums; }
			#workarea table.kdx-table td.kdx-qty-in,
			#workarea table.kdx-table td.kdx-qty-out {
				font-size: inherit;
				font-variant-numeric: tabular-nums;
			}
			#workarea table.kdx-table td.kdx-kardex-nf { color: #0369a1; font-weight: 600; }
			.kdx-alert { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; padding: 8px 12px; border-radius: 8px; margin-bottom: 1rem; display: none; }
			.kdx-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; margin-top: 0.75rem; }
			.kdx-meta { font-size: 0.8rem; color: #64748b; padding: 10px 14px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; background: #fafafa; }
		</style>
		<div class="kdx-wrap">
			<div class="kdx-header">
				<h1>Extrato SKU</h1>
				<div class="sub">
					Recall / rastreio: entradas (NF, fornecedor, lote fab., quantidade)
					e saídas (pedido, destinatário, quantidade, lote fab.).
					Filtro opcional por lote fabricante.
				</div>
			</div>
			<div id="exsku-alert" class="kdx-alert"></div>
			<div class="kdx-card">
				<div class="kdx-filter-title">Filtros</div>
				<div class="kdx-filters">
					<label style="min-width:14rem">Empresa<sup>*</sup>
						<select id="exsku-company"></select>
					</label>
					<label style="min-width:16rem;flex:1 1 16rem">Código item / descrição
						<div style="display:flex;gap:4px">
							<input type="search" id="exsku-busca-prod" placeholder="Digite e busque…" autocomplete="off">
							<button type="button" class="button" id="exsku-btn-buscar">Buscar</button>
						</div>
					</label>
					<label>SKU
						<input type="text" id="exsku-sku" placeholder="Preenchido ao selecionar" autocomplete="off">
					</label>
					<label>Lote fabricante
						<input type="text" id="exsku-lot" placeholder="Opcional" autocomplete="off">
					</label>
					<button type="button" class="button" id="exsku-consultar">Consultar</button>
				</div>
				<div id="exsku-prod-pick" class="kdx-prod-pick"></div>
				<div id="exsku-prod-sel" class="mts" style="color:#0369a1;font-weight:600;margin-top:6px;word-break:break-word"></div>
				<input type="hidden" id="exsku-product-id" value="">
				<div class="kdx-actions">
					<button type="button" class="button" id="exsku-csv" disabled>Exportar CSV</button>
				</div>
			</div>
			<div class="kdx-card" style="padding:0" id="exsku-preview-card">
				<div id="exsku-preview">
					<p class="mts" style="color:#64748b;padding:12px 14px">Use «Consultar» para carregar o extrato.</p>
				</div>
			</div>
		</div>
	`;

	const companySel = $('#exsku-company', work);
	const pickBox = $('#exsku-prod-pick', work);
	const prodSel = $('#exsku-prod-sel', work);
	const productIdEl = $('#exsku-product-id', work);
	const skuEl = $('#exsku-sku', work);
	const csvBtn = $('#exsku-csv', work);
	const alertEl = $('#exsku-alert', work);

	await loadCompanies(companySel);

	function showAlert(msg) {
		if (!msg) {
			alertEl.style.display = 'none';
			alertEl.textContent = '';
			return;
		}
		alertEl.textContent = msg;
		alertEl.style.display = 'block';
	}

	function clearProduct() {
		productIdEl.value = '';
		prodSel.textContent = '';
		pickBox.style.display = 'none';
		pickBox.innerHTML = '';
		csvBtn.disabled = true;
	}

	companySel.addEventListener('change', () => {
		clearProduct();
		showAlert('');
	});

	async function doSearch() {
		showAlert('');
		const companyId = companySel.value?.trim();
		const term = $('#exsku-busca-prod', work)?.value?.trim() || skuEl.value?.trim();
		if (!companyId) {
			notify('Selecione a empresa.', 'Extrato SKU');
			return;
		}
		if (!term) {
			notify('Digite SKU ou nome para buscar.', 'Extrato SKU');
			return;
		}
		const mb = msgbox({ animation: true, text: 'Buscando produtos…' });
		try {
			const rows = await searchProducts(companyId, term);
			pickBox.innerHTML = '';
			if (!rows.length) {
				pickBox.style.display = 'none';
				notify('Nenhum produto encontrado.', 'Extrato SKU');
				return;
			}
			for (const p of rows) {
				const btn = document.createElement('button');
				btn.type = 'button';
				const sku = skuLabel(p);
				const name = shortLabel(p.name || '(sem nome)', 70);
				btn.textContent = `${sku} — ${name} (Posição ${p.id})`;
				btn.title = `${sku} — ${p.name || ''} (Posição ${p.id})`;
				btn.addEventListener('click', () => {
					productIdEl.value = String(p.id);
					skuEl.value = sku;
					prodSel.textContent = `Selecionado: ${sku} — ${shortLabel(p.name || '', 90)} (Posição ${p.id})`;
					prodSel.title = `${sku} — ${p.name || ''} (Posição ${p.id})`;
					pickBox.style.display = 'none';
					$('#exsku-busca-prod', work).value = sku;
				});
				pickBox.appendChild(btn);
			}
			pickBox.style.display = 'block';
		} finally {
			mb.destroy();
		}
	}

	$('#exsku-btn-buscar', work)?.addEventListener('click', () => { void doSearch(); });
	$('#exsku-busca-prod', work)?.addEventListener('keydown', (ev) => {
		if (ev.key === 'Enter') {
			ev.preventDefault();
			void doSearch();
		}
	});

	$('#exsku-consultar', work)?.addEventListener('click', async () => {
		showAlert('');
		const packed = collectQuery(work, 'json');
		if (!packed) return;
		const mb = msgbox({ animation: true, text: 'Consultando extrato…' });
		try {
			const res = await api.sku_extract_report(packed.companyId, `?${packed.qs}`);
			const raw = await res.text();
			if (!res.ok) {
				const err = formatBiHttpError(res.status, raw) || res.statusText || `HTTP ${res.status}`;
				showAlert(err);
				notify(err, 'Extrato SKU');
				csvBtn.disabled = true;
				return;
			}
			let data;
			try {
				data = JSON.parse(raw);
			} catch {
				showAlert('Resposta inválida da API.');
				return;
			}
			renderResult($('#exsku-preview', work), data);
			csvBtn.disabled = false;
		} catch (e) {
			const msg = String(e?.message || e);
			showAlert(msg);
			notify(msg, 'Extrato SKU');
			csvBtn.disabled = true;
		} finally {
			mb.destroy();
		}
	});

	csvBtn?.addEventListener('click', async () => {
		showAlert('');
		const packed = collectQuery(work, 'csv');
		if (!packed) return;
		const mb = msgbox({ animation: true, text: 'Gerando CSV…' });
		try {
			const res = await api.sku_extract_report(packed.companyId, `?${packed.qs}`);
			if (!res.ok) {
				const raw = await res.text();
				const err = formatBiHttpError(res.status, raw) || res.statusText;
				showAlert(err);
				notify(err, 'Extrato SKU');
				return;
			}
			const blob = await res.blob();
			const a = document.createElement('a');
			a.href = URL.createObjectURL(blob);
			const safe = String(packed.skuHint || 'sku').replace(/[^\w.-]+/g, '_').slice(0, 40);
			a.download = `extrato-sku-${safe}.csv`;
			a.click();
			URL.revokeObjectURL(a.href);
		} catch (e) {
			notify(String(e?.message || e), 'Extrato SKU');
		} finally {
			mb.destroy();
		}
	});
}
